import { MindMapNode } from '../types';

// Canonical sibling sort — siblingOrder, then title as a tiebreaker. Matches the sort key already
// used elsewhere for ordering siblings (e.g. YouTubeManagerModal's branch list), so "document
// order" here lines up with what the Outline panel already shows, without needing to duplicate
// its exact tree-building code.
function siblingSort(a: MindMapNode, b: MindMapNode): number {
    return (a.siblingOrder ?? 0) - (b.siblingOrder ?? 0) || (a.title || '').localeCompare(b.title || '');
}

// Flattens a notebook's notes into full document (reading) order: a pre-order depth-first walk of
// the tree, each level sorted the same way siblings are ordered everywhere else. Deliberately
// ignores any single Outline panel instance's local collapsed/expanded state — this is the note's
// place in the whole document, not what happens to be visible in whichever panel triggered a
// floating preview.
export function getDocumentOrder(nodes: MindMapNode[]): string[] {
    const nodeIds = new Set(nodes.map(n => n.id));
    const byParent = new Map<string | null, MindMapNode[]>();
    for (const n of nodes) {
        const key = n.parentId && nodeIds.has(n.parentId) ? n.parentId : null;
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(n);
    }
    for (const list of byParent.values()) list.sort(siblingSort);

    const order: string[] = [];
    const visit = (parentId: string | null) => {
        for (const n of byParent.get(parentId) ?? []) {
            order.push(n.id);
            visit(n.id);
        }
    };
    visit(null);
    return order;
}

// Previous/next note id in full document (reading) order, or null at either end (or if nodeId
// isn't in this list at all — e.g. a cross-notebook stub whose real siblings haven't loaded yet).
export function getAdjacentNoteIds(nodes: MindMapNode[], nodeId: string): { prevId: string | null; nextId: string | null } {
    const order = getDocumentOrder(nodes);
    const idx = order.indexOf(nodeId);
    if (idx === -1) return { prevId: null, nextId: null };
    return {
        prevId: idx > 0 ? order[idx - 1] : null,
        nextId: idx < order.length - 1 ? order[idx + 1] : null,
    };
}
